import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCaptureContext,
  createPlaybackContext,
  createPlaybackScheduler,
  startMicCapture,
} from '../utils/liveAudio';

function apiUrlToWsUrl(apiUrl) {
  const url = new URL(apiUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/api/live';
  url.search = '';
  url.hash = '';
  return url.toString();
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const base64 = result.split(',')[1] || '';
        resolve(base64);
      } else {
        reject(new Error('Failed to encode frame'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export default function useLiveSession({ onSessionEnd, onError } = {}) {
  const [status, setStatus] = useState('idle');
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);
  const [micMuted, setMicMuted] = useState(false);
  const [videoMode, setVideoMode] = useState('none');
  const [videoStream, setVideoStream] = useState(null);
  const [lastFrameAt, setLastFrameAt] = useState(0);

  const wsRef = useRef(null);
  const captureCtxRef = useRef(null);
  const playbackCtxRef = useRef(null);
  const playbackRef = useRef(null);
  const micRef = useRef(null);
  const videoStreamRef = useRef(null);
  const videoIntervalRef = useRef(null);
  const videoElRef = useRef(null);
  const canvasRef = useRef(null);
  const transcriptBufRef = useRef({ questioner: '', assistant: '' });
  const micMutedRef = useRef(false);
  const endingRef = useRef(false);

  const appendTranscriptDelta = useCallback((role, text) => {
    if (!text) return;
    const key = role === 'assistant' ? 'assistant' : 'questioner';
    transcriptBufRef.current[key] += text;

    setTranscript((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === key) {
        next[next.length - 1] = { ...last, message: last.message + text };
        return next;
      }
      return [...next, { role: key, message: text, via: 'live' }];
    });
  }, []);

  const flushTranscriptTurn = useCallback(() => {
    transcriptBufRef.current = { questioner: '', assistant: '' };
  }, []);

  const stopVideoCapture = useCallback(() => {
    if (videoIntervalRef.current) {
      clearInterval(videoIntervalRef.current);
      videoIntervalRef.current = null;
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((t) => t.stop());
      videoStreamRef.current = null;
    }
    if (videoElRef.current) {
      videoElRef.current.srcObject = null;
      videoElRef.current = null;
    }
    setVideoStream(null);
    setLastFrameAt(0);
  }, []);

  const sendVideoFrame = useCallback(async () => {
    const ws = wsRef.current;
    const video = videoElRef.current;
    const canvas = canvasRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !video || !canvas) return;
    if (video.readyState < 2) return;

    const ctx = canvas.getContext('2d');
    const maxDim = 1024;
    let { videoWidth: w, videoHeight: h } = video;
    if (!w || !h) return;
    if (w > maxDim || h > maxDim) {
      const scale = maxDim / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.75));
    if (!blob) return;
    const data = await blobToBase64(blob);
    ws.send(JSON.stringify({ type: 'video', data }));
    setLastFrameAt(Date.now());
  }, []);

  const startVideoCapture = useCallback(
    async (mode) => {
      stopVideoCapture();
      if (mode === 'none') return;

      let stream;
      if (mode === 'screen') {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 1 },
          audio: false,
        });
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      }

      videoStreamRef.current = stream;
      setVideoStream(stream);
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      videoElRef.current = video;

      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        setVideoMode('none');
      });

      videoIntervalRef.current = setInterval(() => {
        sendVideoFrame().catch(() => {});
      }, 1000);
    },
    [sendVideoFrame, stopVideoCapture]
  );

  const cleanup = useCallback(() => {
    stopVideoCapture();
    if (micRef.current) {
      micRef.current.stop();
      micRef.current = null;
    }
    if (captureCtxRef.current) {
      captureCtxRef.current.close().catch(() => {});
      captureCtxRef.current = null;
    }
    if (playbackRef.current) {
      playbackRef.current.clearPlayback();
    }
    if (playbackCtxRef.current) {
      playbackCtxRef.current.close().catch(() => {});
      playbackCtxRef.current = null;
      playbackRef.current = null;
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'stop' }));
        }
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
    setStatus('idle');
    setMicMuted(false);
    micMutedRef.current = false;
    setVideoMode('none');
  }, [stopVideoCapture]);

  const endSession = useCallback(() => {
    endingRef.current = true;
    cleanup();
    if (onSessionEnd) onSessionEnd();
  }, [cleanup, onSessionEnd]);

  const startSession = useCallback(async () => {
    endingRef.current = false;
    setError(null);
    setTranscript([]);
    transcriptBufRef.current = { questioner: '', assistant: '' };
    setStatus('connecting');

    const token = localStorage.getItem('access');
    if (!token) {
      const msg = 'Not authenticated';
      setError(msg);
      setStatus('error');
      if (onError) onError(msg);
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const wsUrl = apiUrlToWsUrl(apiUrl);

    try {
      const captureCtx = createCaptureContext();
      captureCtxRef.current = captureCtx;
      if (captureCtx.state === 'suspended') {
        await captureCtx.resume();
      }

      const playbackCtx = createPlaybackContext();
      playbackCtxRef.current = playbackCtx;
      playbackRef.current = createPlaybackScheduler(playbackCtx);
      if (playbackCtx.state === 'suspended') {
        await playbackCtx.resume();
      }

      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token }));
      };

      ws.onmessage = async (event) => {
        if (event.data instanceof ArrayBuffer) {
          playbackRef.current?.enqueuePcm(event.data);
          return;
        }

        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        switch (data.type) {
          case 'auth_ok':
            setStatus('starting');
            break;
          case 'ready':
            setStatus('live');
            micRef.current = await startMicCapture(captureCtx, (buffer) => {
              if (micMutedRef.current) return;
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(buffer);
              }
            });
            break;
          case 'transcript':
            appendTranscriptDelta(data.role, data.text);
            break;
          case 'turn_complete':
            flushTranscriptTurn();
            break;
          case 'interrupted':
            playbackRef.current?.clearPlayback();
            break;
          case 'saved':
            break;
          case 'error':
            setError(data.message || 'Live session error');
            if (onError) onError(data.message);
            break;
          default:
            break;
        }
      };

      ws.onerror = () => {
        const msg = 'WebSocket connection failed';
        setError(msg);
        setStatus('error');
        if (onError) onError(msg);
      };

      ws.onclose = () => {
        if (!endingRef.current) {
          cleanup();
        }
      };
    } catch (err) {
      const msg = err.message || 'Failed to start live session';
      setError(msg);
      setStatus('error');
      cleanup();
      if (onError) onError(msg);
    }
  }, [
    appendTranscriptDelta,
    cleanup,
    flushTranscriptTurn,
    onError,
  ]);

  const toggleMic = useCallback(() => {
    setMicMuted((prev) => {
      micMutedRef.current = !prev;
      return !prev;
    });
  }, []);

  const changeVideoMode = useCallback(
    async (mode) => {
      setVideoMode(mode);
      if (status === 'live') {
        try {
          await startVideoCapture(mode);
        } catch (err) {
          setError(err.message || 'Could not access camera/screen');
          setVideoMode('none');
        }
      }
    },
    [startVideoCapture, status]
  );

  useEffect(() => () => cleanup(), [cleanup]);

  return {
    status,
    transcript,
    error,
    micMuted,
    videoMode,
    videoStream,
    lastFrameAt,
    startSession,
    endSession,
    toggleMic,
    changeVideoMode,
    isActive: status === 'live' || status === 'starting' || status === 'connecting',
  };
}
